/**
 * Базовый класс кнопки с hover/press эффектами
 */

import * as PIXI from 'pixi.js';
import { TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';

export interface ButtonOptions {
  width?: number;
  height?: number;
  radius?: number;
  fillColor?: number;
  strokeColor?: number;
  text?: string;
  fontSize?: number;
  icon?: string;
  interactive?: boolean;
  align?: 'left' | 'center' | 'right';
  lineHeight?: number;
}

export class Button extends PIXI.Container {
  private background: PIXI.Graphics;
  private label: PIXI.Text | null = null;
  private options: Required<ButtonOptions>;
  private isPressed = false;
  private isHovered = false;

  constructor(options: ButtonOptions = {}) {
    super();
    this.options = {
      width: options.width ?? 100,
      height: options.height ?? 50,
      radius: options.radius ?? 10,
      fillColor: options.fillColor ?? COLORS.buttonPrimary,
      strokeColor: options.strokeColor ?? COLORS.purple,
      text: options.text ?? '',
      fontSize: options.fontSize ?? 16,
      icon: options.icon ?? '',
      interactive: options.interactive ?? true,
      align: options.align ?? 'center',
      lineHeight: options.lineHeight ?? options.fontSize ?? 16,
    };
    this.background = new PIXI.Graphics();
    this.addChild(this.background);
    this.build();
    if (this.options.interactive) {
      this.setupInteractivity();
    }
  }

  private build(): void {
    this.drawBackground();
    if (this.options.text) {
      this.label = new PIXI.Text(
        this.options.text,
        new TextStyle({
          fontFamily: '"Jost"',
          fontSize: this.options.fontSize,
          fill: COLORS.textPrimary,
          fontWeight: 'bold',
          align: this.options.align,
          lineHeight: this.options.lineHeight,
        })
      );
      this.label.anchor.set(0.5);
      this.label.x = this.options.width / 2;
      this.label.y = this.options.height / 2;
      this.addChild(this.label);
    }
  }

  private drawBackground(): void {
    const g = this.background;
    g.clear();
    const color = this.isPressed
      ? this.options.fillColor * 0.7
      : this.isHovered
        ? COLORS.buttonPrimaryHover
        : this.options.fillColor;
    g.beginFill(color);
    if (this.options.radius > 0) {
      g.drawRoundedRect(0, 0, this.options.width, this.options.height, this.options.radius);
    } else {
      g.drawRect(0, 0, this.options.width, this.options.height);
    }
    g.endFill();
    if (this.isHovered && !this.isPressed) {
      g.lineStyle(2, this.options.strokeColor, 1);
      if (this.options.radius > 0) {
        g.drawRoundedRect(0, 0, this.options.width, this.options.height, this.options.radius);
      } else {
        g.drawRect(0, 0, this.options.width, this.options.height);
      }
    }
  }

  private setupInteractivity(): void {
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointerdown', () => {
      this.isPressed = true;
      this.drawBackground();
      this.scale.set(0.95);
    });
    this.on('pointerup', () => {
      this.isPressed = false;
      this.drawBackground();
      this.scale.set(1);
    });
    this.on('pointerupoutside', () => {
      this.isPressed = false;
      this.drawBackground();
      this.scale.set(1);
    });
    this.on('pointerenter', () => {
      this.isHovered = true;
      this.drawBackground();
      this.scale.set(1.05);
    });
    this.on('pointerleave', () => {
      this.isHovered = false;
      this.isPressed = false;
      this.drawBackground();
      this.scale.set(1);
    });
  }

  setEnabled(enabled: boolean): void {
    this.eventMode = enabled ? 'static' : 'auto';
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1 : 0.5;
  }

  setText(text: string): void {
    if (this.label) {
      this.label.text = text;
    } else if (text) {
      this.options.text = text;
      this.build();
    }
  }
}
